function getCollectionFilterParams(form) {
  const params = new URLSearchParams();

  Array.from(form.elements).forEach((element) => {
    if (element.type === "checkbox" && element.checked) params.append(element.name, element.value);
  });

  const sortRadio = form.querySelector('input[name="sort_by"]:checked');
  if (sortRadio) params.set("sort_by", sortRadio.value);

  return params;
}

function isCollectionFilterKey(key) {
  return key === "sort_by" || key === "page" || key.startsWith("filter.");
}

function mergeCollectionParams(filterParams) {
  const merged = new URLSearchParams(location.search);

  Array.from(merged.keys()).forEach((key) => {
    if (isCollectionFilterKey(key)) merged.delete(key);
  });

  for (const [key, value] of filterParams.entries()) {
    merged.append(key, value);
  }

  return merged;
}

function updateCollectionHistory(params) {
  const search = mergeCollectionParams(params).toString();
  history.pushState(
    { searchParams: search },
    "",
    search ? `${location.pathname}?${search}` : location.pathname
  );
}

class CollectionGrid extends HTMLElement {
  #controller = null;

  beginLoading() {
    this.#controller?.abort();
    this.#controller = null;
    this.classList.add("loading");
  }

  endLoading() {
    this.classList.remove("loading");
    this.dispatchEvent(new CustomEvent("collectiongrid:rendered", { bubbles: true }));
  }

  render(params = new URLSearchParams()) {
    this.beginLoading();
    const controller = new AbortController();
    this.#controller = controller;

    const url = new URL(location.pathname, location.origin);

    url.searchParams.set("section_id", this.id);

    for (const [param, value] of params.entries()) {
      url.searchParams.append(param, value);
    }

    return fetch(url.href, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Collection request failed with status ${response.status}`);
        }
        return response.text();
      })
      .then((html) => this.renderFromSection(html))
      .catch((error) => {
        if (error.name == "AbortError") {
          return;
        }
        console.error(error);
      })
      .finally(() => {
        if (this.#controller !== controller) return;

        this.#controller = null;
        this.endLoading();
      });
  }

  parseSection(html) {
    if (!html) {
      throw new Error(`Collection response is missing section ${this.id}`);
    }

    return this.sectionFrom(new DOMParser().parseFromString(html, "text/html"));
  }

  sectionFrom(doc) {
    const fragment = doc.getElementById(this.id);
    if (!fragment) {
      throw new Error(`Collection response is missing #${this.id}`);
    }

    return fragment;
  }

  applySection(fragment) {
    this.innerHTML = fragment.innerHTML;
  }

  renderFromSection(html) {
    this.applySection(this.parseSection(html));
  }
}

customElements.define("collection-grid", CollectionGrid);

class CollectionFilters extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();
    this.form = this.querySelector("form");
    this.sortWrapper = this.querySelector(".collection-sort");
    this.sortModal = this.querySelector(".collection-sort-modal");

    this.applyFilteredStateFromURL();
    this.applyCheckedFromURL();

    if (!this.form) return;

    this.onInput = this.onInput.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleGridRendered = this.handleGridRendered.bind(this);
    this.form.addEventListener("change", this.onInput);
    document.addEventListener("click", this.handleDocumentClick, true);
    document.addEventListener("collectiongrid:rendered", this.handleGridRendered);
  }

  disconnectedCallback() {

    super.disconnectedCallback();
    this.form?.removeEventListener("change", this.onInput);
    document.removeEventListener("click", this.handleDocumentClick, true);
    document.removeEventListener("collectiongrid:rendered", this.handleGridRendered);
  }

  handleDocumentClick(event) {
    if (!this.sortWrapper || !this.sortModal) return;
    if (!this.sortWrapper.contains(event.target)) {
      this.sortWrapper.classList.remove("is-open");
    }
  }

  handleGridRendered() {
    this.classList.remove("loading");
  }

  applyCheckedFromURL() {
    const params = new URLSearchParams(window.location.search);
    const buttons = this.querySelectorAll('.sales-button');
    buttons.forEach(button => {
      const input = button.querySelector('input');
      if (!input) return;
      if (params.toString().includes(input.value)) {
        button.classList.add('is-checked');
      }
    });
  }

  toggleSort() {
    if (!this.sortWrapper) return;
    if (!this.sortWrapper.classList.contains("is-open")) {
      this.sortWrapper.classList.add("is-open");
    } else {
      this.sortWrapper.classList.remove("is-open");
    }
  }

  clearAll() {
    this.form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = false;
      const label = cb.closest("label");
      if (label) label.classList.remove("is-checked");
    });

    const sortChecked = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortChecked) sortChecked.checked = false;

    this.classList.remove("is-filtered");
    this.submitCombinedParams();
  }

  onInput(event) {
    if (event.target.type === "checkbox") {
      const label = event.target.closest("label");
      if (label) {
        if (event.target.checked) label.classList.add("is-checked");
        else label.classList.remove("is-checked");
      }
      this.submitCombinedParams();
      return;
    }

    if (event.target.name === "sort_by") {
      this.submitCombinedParams();
      if (this.sortWrapper) this.sortWrapper.classList.remove("is-open");
      return;
    }
  }

  submitCombinedParams() {
    const params = getCollectionFilterParams(this.form);

    this.classList.add("loading");
    updateCollectionHistory(params);
    this.updateCollection(params);
    this.classList.toggle(
      "is-filtered",
      Array.from(params.keys()).some((key) => key.startsWith("filter."))
    );
  }

  updateCollection(params) {
    const collection = document.querySelector("collection-grid");
    if (collection) collection.render(params);
  }

  applyFilteredStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const hasRealFilters = Array.from(params.keys()).some((key) => key.startsWith("filter."));
    if (hasRealFilters) {
      this.classList.add("is-filtered");
    } else {
      this.classList.remove("is-filtered");
    }
  }
}

customElements.define("collection-filters", CollectionFilters);

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

class CollectionFiltersDrawer extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();
    this.restoreState = null;
    this.requestController = null;
    this.previouslyFocused = null;
    this.inertedElements = [];
    this.form = this.querySelector("form");
    this.onInput = this.onInput.bind(this);
    this.onExternalInput = this.onExternalInput.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.form?.addEventListener("change", this.onInput);
    document.addEventListener("change", this.onExternalInput);
    window.addEventListener("popstate", this.onPopState);
    document.addEventListener("keydown", this.onKeydown);
  }

  disconnectedCallback() {

    super.disconnectedCallback();
    this.form?.removeEventListener("change", this.onInput);
    document.removeEventListener("change", this.onExternalInput);
    this.requestController?.abort();
    window.removeEventListener("popstate", this.onPopState);
    document.removeEventListener("keydown", this.onKeydown);
    this.setBackgroundInert(false);
  }

  get scroller() {
    return this.querySelector(".collection-filters-content");
  }

  get grid() {
    return document.querySelector("collection-grid");
  }

  get checkboxes() {
    return Array.from(this.form?.elements ?? []).filter((element) => element.type === "checkbox");
  }

  onExternalInput(event) {
    if (!this.form || event.target.form !== this.form || this.contains(event.target)) return;

    this.restoreState = null;
    this.submitCombinedParams();
  }

  applySaleTiers(doc) {
    const current = document.querySelector("[data-collection-sale-tiers]");
    const next = doc?.querySelector("[data-collection-sale-tiers]");
    if (current && next) current.replaceWith(next);
  }

  onInput(event) {
    const target = event.target;
    if (target.type !== "checkbox" && target.name !== "sort_by") return;

    const scroller = this.scroller;
    const anchorElement = target.closest("label") || target;

    this.restoreState = {
      name: target.name,
      value: target.value,
      hadFocus: document.activeElement === target,
      scrollTop: scroller?.scrollTop || 0,
      viewportOffset: scroller
        ? anchorElement.getBoundingClientRect().top - scroller.getBoundingClientRect().top
        : 0,
    };

    this.submitCombinedParams();
  }

  submitCombinedParams() {
    const previous = new URLSearchParams(location.search);
    const params = getCollectionFilterParams(this.form);

    updateCollectionHistory(params);
    this.render(params, previous);
  }

  render(params = null, previous = null) {
    const currentBody = this.querySelector("#collection-filters-body");
    if (!currentBody) return;

    const grid = this.grid;

    this.classList.add("state--loading");
    this.setAttribute("aria-busy", "true");
    currentBody.setAttribute("inert", "");
    grid?.beginLoading();

    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;

    const nextParams = params instanceof URLSearchParams ? params : new URLSearchParams(location.search);

    const url = new URL(location.pathname, location.origin);

    for (const [key, value] of nextParams.entries()) {
      if (key === "sections" || key === "section_id") continue;
      url.searchParams.append(key, value);
    }

    const sections = ["collection-filters-drawer"];
    if (grid?.id) sections.push(grid.id);
    url.searchParams.set("sections", sections.join(","));

    return fetch(url.href, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Filter request failed with status ${response.status}`);
        }
        return response.json();
      })
      .then((sectionsHTML) => {
        const drawerHTML = sectionsHTML["collection-filters-drawer"];
        if (!drawerHTML) {
          throw new Error("Filter drawer response is missing its content");
        }

        const doc = new DOMParser().parseFromString(drawerHTML, "text/html");
        const nextBody = doc.querySelector("#collection-filters-body");
        if (!nextBody) {
          throw new Error("Filter drawer response is missing its filter list");
        }

        const gridHTML = grid?.id ? sectionsHTML[grid.id] : null;
        const gridDoc = gridHTML ? new DOMParser().parseFromString(gridHTML, "text/html") : null;
        const nextGrid = gridDoc ? grid.sectionFrom(gridDoc) : null;

        currentBody.replaceWith(nextBody);

        const nextCount = doc.querySelector("[data-collection-filters-count]");
        const currentCount = this.querySelector("[data-collection-filters-count]");
        if (nextCount && currentCount) {
          currentCount.textContent = nextCount.textContent;
        }

        this.restoreAfterRender();

        if (nextGrid) grid.applySection(nextGrid);
        this.applySaleTiers(gridDoc);
      })
      .catch((error) => {
        if (error.name == "AbortError") return;
        console.error(error);
        if (previous) this.rollback(previous);
      })
      .finally(() => {
        if (this.requestController !== controller) return;

        this.requestController = null;
        this.classList.remove("state--loading");
        this.removeAttribute("aria-busy");
        this.querySelector("#collection-filters-body")?.removeAttribute("inert");
        grid?.endLoading();
      });
  }

  rollback(previous) {
    this.restoreState = null;

    const search = previous.toString();
    history.replaceState(
      { searchParams: search },
      "",
      search ? `${location.pathname}?${search}` : location.pathname
    );

    this.restoreFormState(previous);
  }

  restoreFormState(params) {
    if (!this.form) return;

    const selected = new Set();
    for (const [key, value] of params.entries()) {
      selected.add(`${key}=${value}`);
    }

    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = selected.has(`${checkbox.name}=${checkbox.value}`);
    });

    const sort = params.get("sort_by");
    this.form.querySelectorAll('input[name="sort_by"]').forEach((radio) => {
      radio.checked = radio.value === sort;
    });
  }

  restoreAfterRender() {
    const restoreState = this.restoreState;
    if (!restoreState) return;
    this.restoreState = null;

    const scroller = this.scroller;
    if (!scroller) return;

    const selector = `input[name="${CSS.escape(restoreState.name)}"][value="${CSS.escape(restoreState.value)}"]`;
    const nextInput = this.querySelector(selector);

    if (nextInput && restoreState.hadFocus) {
      nextInput.focus({ preventScroll: true });
    }

    requestAnimationFrame(() => {
      if (!nextInput) {
        scroller.scrollTop = restoreState.scrollTop;
        return;
      }

      const nextAnchorElement = nextInput.closest("label") || nextInput;
      const nextOffset = nextAnchorElement.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      scroller.scrollTop += nextOffset - restoreState.viewportOffset;
    });
  }

  onPopState(event) {
    this.restoreState = null;
    this.render(new URLSearchParams(event.state?.searchParams ?? location.search));
  }

  clearAll() {
    if (!this.form) return;

    const previous = new URLSearchParams(location.search);

    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    const sortChecked = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortChecked) sortChecked.checked = false;

    this.restoreState = null;

    const params = new URLSearchParams();

    updateCollectionHistory(params);
    this.render(params, previous);
  }

  onKeydown(event) {
    if (!this.classList.contains("state--active")) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key === "Tab") this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = Array.from(this.querySelectorAll(FOCUSABLE_SELECTOR));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setBackgroundInert(inert) {
    if (inert) {
      if (this.inertedElements.length) return;

      this.inertedElements = Array.from(document.body.children).filter(
        (child) => !child.contains(this) && !child.hasAttribute("inert")
      );
      this.inertedElements.forEach((child) => child.setAttribute("inert", ""));
      return;
    }

    this.inertedElements.forEach((child) => child.removeAttribute("inert"));
    this.inertedElements = [];
  }

  show() {
    if (this.classList.contains("state--active")) return;

    this.previouslyFocused = document.activeElement;
    this.classList.add("state--active");
    this.setBackgroundInert(true);

    requestAnimationFrame(() => {
      this.querySelector("#collection-filters-drawer")?.focus({ preventScroll: true });
    });
  }

  hide() {
    if (!this.classList.contains("state--active")) return;

    this.classList.remove("state--active");
    this.setBackgroundInert(false);
    this.previouslyFocused?.focus?.({ preventScroll: true });
    this.previouslyFocused = null;
  }
}
customElements.define("collection-filters-drawer", CollectionFiltersDrawer);
